import { Avatar, Box, Stack, Typography } from "@mui/material";
import {
  CommentsCount,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
} from "./style";
import { ArrowDownIcon, CloseIcon } from "../../../assets/Icons";
import { memo, useCallback, useState, useEffect } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { LexicalTextEditorTheme } from "../../../utils/theme";
import { useTheme } from "styled-components";
import ToolbarPlugin from "../textEditorPlugins/toolbarPlugin";
import SendButton from "../textEditorPlugins/sendButton";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import "./textEditorStyle.css";
import { DiscoveryItem } from "../../../store/discovery/types";
import { useDispatch, useSelector } from "react-redux";
import { MetaDataSagaActions } from "../../../store/metadata/sagas";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import {
  adDetailsSelector,
  isLoadingSelector,
} from "../../../store/metadata/selector";
import { commentsType } from "../../../store/metadata/types";
import { formatCreatedDate } from "../../../utils/helpers";
import { CustomButton } from "../../../globalStyle";
import { userDetailsSelector } from "../../../store/account/selector";
import {
  $getRoot,
  CLEAR_EDITOR_COMMAND,
  COMMAND_PRIORITY_HIGH,
  EditorState,
} from "lexical";
import { KEY_ENTER_COMMAND } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import DeleteConfirmationModal from "../../modals/deleteConfirmationModal";

const editorConfig = {
  namespace: "React MarkDown Editor Demo",
  onError(error: Error) {
    throw error;
  },
  theme: LexicalTextEditorTheme,
};

const CommentBox = ({ item }: { item: DiscoveryItem }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const [editorText, setEditorText] = useState<string>("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState<string | null>(
    null
  );

  const dispatch = useDispatch();
  const adDetailsData = useSelector(adDetailsSelector);
  const userDetails = useSelector(userDetailsSelector);
  const loadingCommentAction = useSelector(isLoadingSelector);

  const handleAddComment = (comment: string) => {
    dispatch(
      MetaDataSagaActions.addComment({
        _id: item._id,
        payload: { comment }, // HTML from Lexical
      })
    );
  };

  const handleCommentDelete = (commentId: string) => {
    dispatch(MetaDataSagaActions.deleteCommentById(commentId));
  };

  const onChange = useCallback((editorState: EditorState) => {
    const htmlString = editorState.toJSON().root.children
      ? editorState.read(() => $getRoot().getTextContent())
      : "";
    setEditorText(htmlString);
  }, []);

  const EnterKeyPlugin = ({ onSend }: { onSend: () => void }) => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
      return editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event: any) => {
          const { shiftKey, key } = event;
          if (key === "Enter" && !shiftKey) {
            event.preventDefault();
            onSend();
            editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
          }
          return true;
        },
        COMMAND_PRIORITY_HIGH
      );
    }, [editor, onSend]);

    return null;
  };

  function cleanLexicalHtml(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Step 1: unwrap <span> tags but track where line breaks occur
    const spans = Array.from(doc.querySelectorAll("span"));

    for (let i = 0; i < spans.length; i++) {
      const span = spans[i];
      const parent = span.parentNode;
      if (!parent) continue;

      // If this span is followed by another span and contains text, insert a <br />
      const next = span.nextSibling;
      const shouldInsertBreak =
        next &&
        next.nodeName === "SPAN" &&
        span.textContent?.trim() &&
        next.textContent?.trim();

      // Move contents out of span
      while (span.firstChild) {
        parent.insertBefore(span.firstChild, span);
      }

      if (shouldInsertBreak) {
        parent.insertBefore(document.createElement("br"), span);
      }

      parent.removeChild(span);
    }

    return doc.body.innerHTML;
  }


  return (
    <Box>
      <StyledAccordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <StyledAccordionSummary
          expandIcon={<ArrowDownIcon />}
          sx={{ padding: "1rem" }}
        >
          Comments
          <CommentsCount>{adDetailsData?.Comments?.length ?? 0}</CommentsCount>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <Box sx={{ height: "200px", overflow: "auto" }}>
            {adDetailsData?.Comments?.map((commentItem: commentsType) => {
              return (
                <Stack
                  key={commentItem.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom="0.313rem"
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.75rem",
                      backgroundColor: theme.colors.background4,
                      width: "fit-content",
                      padding: "0.5rem 0.625rem",
                    }}
                  >
                    <Avatar
                      src={commentItem?.user?.profileImage ?? ""}
                      sx={{ width: "2.5rem", height: "2.5rem" }}
                    />
                    <Stack>
                      <Typography
                        fontSize="0.938rem"
                        color={theme.colors.black2}
                        fontWeight={500}
                        dangerouslySetInnerHTML={{
                          __html: cleanLexicalHtml(commentItem.comment),
                        }}
                        style={{ whiteSpace: "pre-wrap" }}
                      />
                      <Stack direction="row" display={"flex"} gap={2.5}>
                        <Typography
                          fontSize="0.75rem"
                          color={theme.colors.gray1}
                          fontWeight={400}
                        >
                          {commentItem?.user?.fullName} |{" "}
                          {commentItem?.createdAt &&
                            formatCreatedDate(commentItem?.createdAt)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                  {userDetails?._id === commentItem?.user?._id && (
                    <CustomButton
                      disabled={loadingCommentAction}
                      startIcon={<CloseIcon />}
                      onClick={() => {
                        setOpenDeleteModal(true);
                        setCommentIdToDelete(commentItem?.id);
                      }}
                    />
                  )}
                </Stack>
              );
            })}
          </Box>

          <LexicalComposer initialConfig={editorConfig}>
            <Box
              sx={{
                margin: "0 -1rem",
                position: "relative",
                lineHeight: "1.25rem",
              }}
            >
              <RichTextPlugin
                contentEditable={<ContentEditable className="editor-input" />}
                placeholder={
                  <Box
                    sx={{
                      color: theme.colors.black2,
                      fontWeight: "500",
                      position: "absolute",
                      textOverflow: "ellipsis",
                      top: "0.938rem",
                      left: "0.625rem",
                      fontSize: "0.938rem",
                      pointerEvents: "none",
                    }}
                  >
                    Add your comment...
                  </Box>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <ClearEditorPlugin />
              <OnChangePlugin onChange={onChange} />
              <EnterKeyPlugin
                onSend={() => {
                  const comment = editorText.trim();
                  if (comment) {
                    handleAddComment(comment);
                    setEditorText("");
                  }
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "0.5rem",
              }}
            >
              <ToolbarPlugin />
              <SendButton
                handleAddComment={() => {
                  const comment = editorText.trim();
                  if (comment) {
                    handleAddComment(comment);
                    setEditorText("");
                  }
                }}
                editorText={editorText}
              />
            </Box>
          </LexicalComposer>
        </StyledAccordionDetails>
      </StyledAccordion>
      <DeleteConfirmationModal
        isOpen={openDeleteModal}
        handleClose={() => {
          setOpenDeleteModal(false);
          setCommentIdToDelete(null);
        }}
        handleDelete={() => {
          if (commentIdToDelete) {
            handleCommentDelete(commentIdToDelete);
            setCommentIdToDelete(null);
          }
          setOpenDeleteModal(false);
        }}
        modalTitle="Delete Comment"
        modalDescription="Are you sure you want to delete this comment? This action cannot be undone."
        loading={loadingCommentAction}
        btnLabel="Delete"
      />
    </Box>
  );
};

export default memo(CommentBox);

